import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { 
  Users, Mail, Phone, Euro, MapPin, Calendar, Loader2, Search, Filter
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';

const positions = [
  { value: 'all', label: 'Todas las posiciones' },
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const positionColors = {
  GK: 'bg-yellow-500 text-yellow-950',
  DEF: 'bg-blue-500 text-white',
  MID: 'bg-green-500 text-white',
  FWD: 'bg-red-500 text-white',
};

export const AdminPlayersPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    position: 'all',
    nationality: '',
  });

  useEffect(() => {
    loadPlayers();
  }, [filters]);

  const loadPlayers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.position !== 'all') params.position = filters.position;
      if (filters.nationality) params.nationality = filters.nationality;
      
      const response = await api.get('/admin/players', { params });
      setPlayers(response.data);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ position: 'all', nationality: '' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-players-page">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
            Todas las Jugadoras
          </h1>
          <p className="text-slate-500 mt-1">
            {players.length} jugadoras disponibles con datos de contacto
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nacionalidad..."
                  value={filters.nationality}
                  onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select
              value={filters.position}
              onValueChange={(value) => setFilters({ ...filters, position: value })}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Posición" />
              </SelectTrigger>
              <SelectContent>
                {positions.map((pos) => (
                  <SelectItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters}>
              <Filter className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Players Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Jugadora</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Posición</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Club Publicador</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Contacto</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Precio</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Disponibilidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {players.map((player) => (
                    <tr key={player.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={player.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${player.name}`}
                            alt={player.name}
                            className="w-10 h-10 rounded-full bg-slate-100"
                          />
                          <div>
                            <p className="font-semibold text-slate-800">{player.name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar className="w-3 h-3" />
                              {player.age} años
                              <span>•</span>
                              <MapPin className="w-3 h-3" />
                              {player.nationality}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-heading font-bold text-sm ${positionColors[player.position]}`}>
                          {player.position}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-800">{player.club_name}</p>
                        <p className="text-sm text-slate-500">{player.club_contact_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <a href={`mailto:${player.club_contact_email}`} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
                            <Mail className="w-3 h-3" />
                            {player.club_contact_email}
                          </a>
                          {player.club_contact_phone && (
                            <a href={`tel:${player.club_contact_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800">
                              <Phone className="w-3 h-3" />
                              {player.club_contact_phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {player.asking_price ? (
                          <span className="flex items-center gap-1 font-semibold text-slate-800">
                            <Euro className="w-4 h-4 text-indigo-500" />
                            {new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(player.asking_price)}
                          </span>
                        ) : (
                          <span className="text-slate-400">A consultar</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {player.availability === 'transfer' && (
                          <span className="badge-available">Transferencia</span>
                        )}
                        {player.availability === 'loan' && (
                          <span className="badge-loan">Préstamo</span>
                        )}
                        {player.availability === 'both' && (
                          <span className="badge-available">Ambos</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {players.length === 0 && (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No se encontraron jugadoras</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminPlayersPage;
